import { DEFAULT_LAYOUT, type PartialLayoutConfig } from './layout.js';
import { FONT_PAIRS, type ThemeTokens } from './tokens.js';

/**
 * Template presets — CLAUDE.md §5.2: seeded rows, ~30 lines of JSON each.
 * A template is *data*. Adding one must never require touching a component.
 *
 * Retail set: Minimal (default), Sakura, Lotus, Noir, Mekong, Neon.
 *
 * Free plans get two: Minimal and Lotus everywhere, plus Sakura on retail.
 * The rest are the natural tier upsell (§5). A vertical must never end up
 * with fewer than two free templates — `templates.test.ts` asserts it.
 */

export interface ThemeTemplate {
  id: string;
  name: string;
  /** Short pitch shown under the preview card in the onboarding picker. */
  blurb: { en: string; km: string };
  verticals: Array<'retail' | 'fnb' | 'services'>;
  tokens: ThemeTokens;
  /**
   * Optional hand-authored dark palette (§5, v2). Templates that omit it get
   * one derived from their light tokens, so adding a template still means
   * adding data and nothing else.
   */
  dark?: ThemeTokens;
  layout: PartialLayoutConfig;
  /** Locked behind a paid plan (§5 "templates are the natural tier upsell"). */
  premium: boolean;
  position: number;
}

const kantumruy = FONT_PAIRS[0]!;
const notoKhmer = FONT_PAIRS[1]!;
const moulBattambang = FONT_PAIRS[2]!;
const serifKhmer = FONT_PAIRS[4]!;

export const THEME_TEMPLATES: readonly ThemeTemplate[] = [
  {
    id: 'minimal',
    name: 'Minimal',
    blurb: {
      en: 'Clean and neutral. Lets the product photos do the talking.',
      km: 'ស្អាត និងសាមញ្ញ។ ទុកឲ្យរូបភាពទំនិញនិយាយជំនួស។',
    },
    verticals: ['retail', 'fnb', 'services'],
    tokens: {
      'color-primary': '#111827',
      'color-primary-contrast': '#ffffff',
      'color-accent': '#2563eb',
      'color-bg': '#ffffff',
      'color-surface': '#f8fafc',
      'color-text': '#0f172a',
      'color-text-muted': '#64748b',
      'color-border': '#e2e8f0',
      'font-heading': kantumruy.heading,
      'font-body': kantumruy.body,
      radius: '10px',
      'radius-card': '14px',
      'shadow-card': '0 1px 2px rgba(15, 23, 42, 0.06)',
      'button-style': 'solid',
      'header-style': 'plain',
    },
    layout: { ...DEFAULT_LAYOUT },
    premium: false,
    position: 1,
  },
  {
    id: 'sakura',
    name: 'Sakura',
    blurb: {
      en: 'Soft pink and airy — built for cosmetics and womenswear.',
      km: 'ពណ៌ផ្កាឈូកទន់ភ្លន់ — សម្រាប់គ្រឿងសម្អាង និងសម្លៀកបំពាក់នារី។',
    },
    verticals: ['retail'],
    tokens: {
      'color-primary': '#db2777',
      'color-primary-contrast': '#ffffff',
      'color-accent': '#f472b6',
      'color-bg': '#fff7fa',
      'color-surface': '#ffffff',
      'color-text': '#3f1728',
      'color-text-muted': '#9d6b80',
      'color-border': '#fbcfe8',
      'font-heading': serifKhmer.heading,
      'font-body': kantumruy.body,
      radius: '999px',
      'radius-card': '20px',
      'shadow-card': '0 6px 20px rgba(219, 39, 119, 0.10)',
      'button-style': 'pill',
      'header-style': 'soft',
    },
    layout: { ...DEFAULT_LAYOUT, card_image_ratio: '3:4', product_list: 'grid-2' },
    premium: false,
    position: 2,
  },
  {
    id: 'lotus',
    name: 'Lotus',
    blurb: {
      en: 'Warm and calm. Works as well for a menu or a booking list as a shelf.',
      km: 'ក្លិនក្តៅ និងស្ងប់។ ស័ក្តិសមទាំងម៉ឺនុយ បញ្ជីកក់ម៉ោង និងធ្នើទំនិញ។',
    },
    /**
     * Every vertical, and deliberately so: it is the second free template, and
     * §5's "free: 2 templates" has to mean two for an F&B or services shop as
     * well as for a fashion one.
     */
    verticals: ['retail', 'fnb', 'services'],
    tokens: {
      'color-primary': '#0e5c57',
      'color-primary-contrast': '#ffffff',
      'color-accent': '#d97706',
      'color-bg': '#fffdf8',
      'color-surface': '#f6f2ea',
      'color-text': '#1f2421',
      'color-text-muted': '#6b7069',
      'color-border': '#e5ded1',
      'font-heading': kantumruy.heading,
      'font-body': kantumruy.body,
      radius: '12px',
      'radius-card': '16px',
      'shadow-card': '0 2px 10px rgba(31, 36, 33, 0.07)',
      'button-style': 'solid',
      'header-style': 'soft',
    },
    /**
     * `1:1` rather than `3:4`: a dish and a haircut photograph square, and the
     * fnb/services layout constraints only allow `1:1` anyway (§4).
     */
    layout: { ...DEFAULT_LAYOUT, card_image_ratio: '1:1' },
    premium: false,
    position: 3,
  },
  {
    id: 'noir',
    name: 'Noir',
    blurb: {
      en: 'High-contrast black. Streetwear, sneakers, watches.',
      km: 'ខ្មៅផ្ទុយពណ៌ខ្លាំង។ សម្រាប់សម្លៀកបំពាក់ស្ទ្រីត ស្បែកជើង នាឡិកា។',
    },
    verticals: ['retail'],
    tokens: {
      'color-primary': '#f5f5f4',
      'color-primary-contrast': '#0c0a09',
      'color-accent': '#facc15',
      'color-bg': '#0c0a09',
      'color-surface': '#1c1917',
      'color-text': '#fafaf9',
      'color-text-muted': '#a8a29e',
      'color-border': '#292524',
      'font-heading': notoKhmer.heading,
      'font-body': notoKhmer.body,
      radius: '4px',
      'radius-card': '6px',
      'shadow-card': 'none',
      'button-style': 'square',
      'header-style': 'bold',
    },
    layout: { ...DEFAULT_LAYOUT, product_list: 'grid-2', card_show: ['name', 'price', 'compare_at'] },
    premium: true,
    position: 4,
  },
  {
    id: 'mekong',
    name: 'Mekong',
    blurb: {
      en: 'Warm earth tones and Khmer display type. Handicraft and local brands.',
      km: 'ពណ៌ដីកក់ក្ដៅ និងអក្សរខ្មែរបុរាណ។ សម្រាប់សិប្បកម្ម និងម៉ាកក្នុងស្រុក។',
    },
    verticals: ['retail', 'fnb'],
    tokens: {
      'color-primary': '#9a3412',
      'color-primary-contrast': '#fffbeb',
      'color-accent': '#ca8a04',
      'color-bg': '#fffbf5',
      'color-surface': '#fef3c7',
      'color-text': '#3b2415',
      'color-text-muted': '#8a6d55',
      'color-border': '#e7d5bd',
      'font-heading': moulBattambang.heading,
      'font-body': moulBattambang.body,
      radius: '8px',
      'radius-card': '12px',
      'shadow-card': '0 2px 8px rgba(120, 72, 30, 0.10)',
      'button-style': 'solid',
      'header-style': 'soft',
    },
    layout: { ...DEFAULT_LAYOUT, card_image_ratio: '1:1', categories_nav: 'tabs' },
    premium: true,
    position: 5,
  },
  {
    id: 'neon',
    name: 'Neon',
    blurb: {
      en: 'Loud gradients for flash sales and gadget shops.',
      km: 'ពណ៌ភ្លឺច្បាស់សម្រាប់ការបញ្ចុះតម្លៃ និងហាងឧបករណ៍អេឡិចត្រូនិក។',
    },
    verticals: ['retail'],
    tokens: {
      'color-primary': '#7c3aed',
      'color-primary-contrast': '#ffffff',
      'color-accent': '#06b6d4',
      'color-bg': '#0f0a1f',
      'color-surface': '#1b1236',
      'color-text': '#f5f3ff',
      'color-text-muted': '#a78bfa',
      'color-border': '#3b2a6b',
      'font-heading': kantumruy.heading,
      'font-body': kantumruy.body,
      radius: '12px',
      'radius-card': '18px',
      'shadow-card': '0 8px 24px rgba(124, 58, 237, 0.35)',
      'button-style': 'pill',
      'header-style': 'bold',
    },
    layout: { ...DEFAULT_LAYOUT, product_list: 'grid-3', card_image_ratio: '1:1' },
    premium: true,
    position: 6,
  },
];

export const DEFAULT_TEMPLATE_ID = 'minimal';

export function getTemplate(id: string): ThemeTemplate | undefined {
  return THEME_TEMPLATES.find((t) => t.id === id);
}

export function templatesForVertical(vertical: 'retail' | 'fnb' | 'services'): ThemeTemplate[] {
  return THEME_TEMPLATES.filter((t) => t.verticals.includes(vertical)).sort(
    (a, b) => a.position - b.position,
  );
}

/**
 * Premium templates are a Pro unlock (§3, §5).
 *
 * Free and Starter get the non-premium set. That is the same line the feature
 * list draws — `templates_premium` starts at Pro — and it is drawn twice on
 * purpose: this function is what the admin picker calls, and a picker that
 * offers a template the shop cannot save is worse than one that does not show
 * it at all.
 */
export function templatesForPlan(
  vertical: 'retail' | 'fnb' | 'services',
  plan: 'free' | 'starter' | 'pro' | 'business',
): ThemeTemplate[] {
  const all = templatesForVertical(vertical);
  if (plan === 'free' || plan === 'starter') return all.filter((t) => !t.premium);
  return all;
}
